import type { Answer, ExamResult, ModuleId, ModuleResult, ModuleSpec, Question } from './types';
import { getQuestions } from './data';

/**
 * Estructura oficial de la prueba (Comunidad de Madrid, Decreto 5/2024):
 * 60 preguntas, 60 minutos, cuatro módulos con nota mínima por módulo.
 * Suspender un solo módulo implica "no apto" en el conjunto de la prueba.
 */
export const MODULES: ModuleSpec[] = [
  {
    id: 1,
    name: 'Conocimientos de la lengua castellana',
    short: 'Lengua',
    topics: 'Gramática, vocabulario, sinónimos y comprensión.',
    count: 12,
  },
  {
    id: 2,
    name: 'Conocimientos del medio físico',
    short: 'Medio físico',
    topics: 'Callejero de Madrid, distintivos ambientales y uso de navegadores GPS.',
    count: 18,
  },
  {
    id: 3,
    name: 'Conocimientos de accesibilidad',
    short: 'Accesibilidad',
    topics: 'Atención a personas con discapacidad y primeros auxilios.',
    count: 18,
  },
  {
    id: 4,
    name: 'Conocimientos del marco jurídico',
    short: 'Marco jurídico',
    topics: 'Normativa VTC estatal y de la Comunidad de Madrid.',
    count: 12,
  },
];

export const EXAM_MINUTES = 60;

/**
 * Umbral de aprobado por módulo, confirmado en la Orden que desarrolla la prueba
 * (BOCM de 1 de abril de 2025): para superarla hay que obtener un resultado
 * suficiente en cada módulo, con aciertos iguales o superiores al 50% en todos.
 * La calificación final es apto o no apto, sin nota numérica.
 */
export const MODULE_PASS_RATIO = 0.5;

/**
 * La Orden no establece penalización por respuesta incorrecta: dejar una
 * pregunta en blanco nunca es mejor que arriesgar.
 */
export const WRONG_ANSWER_PENALTY = false;

/** La Orden garantiza un mínimo de doce convocatorias al año. */
export const CONVOCATORIAS_POR_AÑO = 12;

export function moduleName(module: ModuleId): string {
  return MODULES.find((spec) => spec.id === module)?.short ?? `Módulo ${module}`;
}

function shuffle<T>(items: T[]): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function questionsByModule(module: ModuleId): Question[] {
  return getQuestions().filter((q) => q.module === module);
}

/** Genera un simulacro completo respetando el reparto oficial por módulo. */
export function buildExam(): Question[] {
  return MODULES.flatMap((spec) => shuffle(questionsByModule(spec.id)).slice(0, spec.count));
}

/** Genera una práctica de un solo módulo. */
export function buildPractice(module: ModuleId, size = 10): Question[] {
  return shuffle(questionsByModule(module)).slice(0, size);
}

/**
 * Sesión de repaso a partir de los identificadores pendientes, respetando su
 * orden de prioridad —las más falladas primero—. Descarta las que ya no estén en
 * el banco: si alguien practicó con licencia y luego la perdió, sus preguntas de
 * pago siguen apuntadas pero ya no se pueden mostrar.
 */
export function buildReview(ids: string[], size = 15): Question[] {
  const porId = new Map(getQuestions().map((q) => [q.id, q]));
  const encontradas: Question[] = [];
  for (const id of ids) {
    const q = porId.get(id);
    if (q) encontradas.push(q);
    if (encontradas.length === size) break;
  }
  return encontradas;
}

export function grade(questions: Question[], answers: Answer[], elapsedMs: number): ExamResult {
  const chosenById = new Map(answers.map((a) => [a.questionId, a.chosen]));

  const modules: ModuleResult[] = MODULES.map((spec) => {
    const inModule = questions.filter((q) => q.module === spec.id);
    const correct = inModule.filter((q) => chosenById.get(q.id) === q.correct).length;
    return {
      module: spec.id,
      correct,
      total: inModule.length,
      passed: inModule.length === 0 || correct >= Math.ceil(inModule.length * MODULE_PASS_RATIO),
    };
  }).filter((m) => m.total > 0);

  const correct = modules.reduce((sum, m) => sum + m.correct, 0);
  const total = modules.reduce((sum, m) => sum + m.total, 0);

  return {
    modules,
    correct,
    total,
    passed: modules.every((m) => m.passed),
    elapsedMs,
  };
}

export function bankSize(): Record<ModuleId, number> {
  return {
    1: questionsByModule(1).length,
    2: questionsByModule(2).length,
    3: questionsByModule(3).length,
    4: questionsByModule(4).length,
  };
}
